import React, { useCallback, useState, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import { Card, Flex, Typography, Button, Form, Spin } from "antd"
import { useTranslation } from "react-i18next"
import { validatePhoneNumber } from "ui/Input/PhoneInput"
import { useParentForm } from "pagesV2/pro/DigitalWaiverAndContracts/SharedWaiver/hooks/useParentForm"
import { WaiverOptions } from "pagesV2/pro/DigitalWaiverAndContracts/SharedWaiver/components/WaiverOptions"
import { WaiverDetails } from "pagesV2/pro/DigitalWaiverAndContracts/SharedWaiver/components/WaiverDetails"
import { ParentGuardianForm } from "pagesV2/pro/DigitalWaiverAndContracts/SharedWaiver/components/ParentGuardianForm"
import { MinorGuestForm } from "pagesV2/pro/DigitalWaiverAndContracts/SharedWaiver/components/MinorGuestForm"
import { SignDocumentModal } from "pagesV2/pro/Embedded/BookingConfirmation/modal/SignDocument"
import AddressIcon from "assets/icons/Address.svg"
import CalendarIcon from "assets/icons/calendar.svg"
import { postWaiverSubmission, checkSubmissionsAvailable } from "store/digital-waiver"
import { getBooking } from "store/bookings"
import { toaster } from "components/common/Toast"
import { routes } from "router"
import bgMan from "assets/imagesV2/logo_man.svg"
import bgWoman from "assets/imagesV2/logo_woman.svg"
import { useTheme } from "context/AntdThemeContext"
import { themes } from "constants/theme"
import styles from "pagesV2/pro/DigitalWaiverAndContracts/SharedWaiver/index.module.css"

const { Title } = Typography

export default function TripBookingWaiverPage() {
  const { t } = useTranslation()
  const { uuid } = useParams()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { theme } = useTheme()
  const { loading: postLoading } = useSelector((store) => store?.digitalWaiver) || {}
  const bookingInfo = useSelector((store) => store.bookings.booking)
  const { id: tripBookingId, loading: bookingLoading } = bookingInfo
  const logoSrc = theme === themes.sportswoman ? bgWoman : bgMan

  const { selected, setSelected, parentData, setParentData, form } = useParentForm()
  const guardianForm = Form.useForm()[0]

  const [isFormValid, setIsFormValid] = useState(false)
  const [showAdultForm, setShowAdultForm] = useState(false)
  const [showGuardianForm, setShowGuardianForm] = useState(false)
  const [showSignDocumentModal, setShowSignDocumentModal] = useState(false)
  const [waiverSubmissionId, setWaiverSubmissionId] = useState(null)
  const [pendingDocuments, setPendingDocuments] = useState([])

  // Fetch trip booking to get numeric trip_booking_id
  useEffect(() => {
    dispatch(getBooking(uuid, true))
  }, [uuid]) // eslint-disable-line

  useEffect(() => {
    if (uuid) {
      dispatch(checkSubmissionsAvailable(uuid))
    }
  }, [uuid]) // eslint-disable-line

  // Reset forms and validity when switching between adult and guardian
  useEffect(() => {
    if (selected === "adult" && form) {
      form.resetFields()
      setShowAdultForm(false)
    } else if (selected === "guardian" && guardianForm) {
      guardianForm.resetFields()
      setShowGuardianForm(false)
    }
    setIsFormValid(false)
  }, [selected]) // eslint-disable-line

  const checkAdultFormValidity = useCallback(() => {
    if (selected !== "adult" || !form) return false
    const values = form.getFieldsValue()
    const requiredFields = ["firstName", "lastName", "email", "dob", "phone"]
    return requiredFields.every((field) => {
      const value = values[field]
      if (field === "phone") return value?.fullNumber?.trim()
      return value?.toString().trim()
    })
  }, [selected, form])

  const checkGuardianFormValidity = useCallback(() => {
    if (selected !== "guardian" || !guardianForm) return false
    if (form) {
      const parentValues = form.getFieldsValue()
      const requiredFields = ["firstName", "lastName", "email", "dob", "phone", "relation"]
      const parentValid = requiredFields.every((field) => {
        const value = parentValues[field]
        if (field === "phone") return value?.fullNumber?.trim()
        return value?.toString().trim()
      })
      if (!parentValid) return false
    }
    const values = guardianForm.getFieldsValue()
    const minors = values.minors || []
    if (minors.length === 0) return false
    return minors.every((minor) => minor.fullName?.trim() && minor.dateOfBirth)
  }, [selected, guardianForm, form])

  useEffect(() => {
    let valid = false
    if (selected === "adult") valid = !showAdultForm
    else if (selected === "guardian") valid = !showGuardianForm
    setIsFormValid(valid)
  }, [selected, showAdultForm, showGuardianForm])

  useEffect(() => {
    if (selected === "adult" && showAdultForm) setIsFormValid(checkAdultFormValidity())
  }, [selected, showAdultForm, checkAdultFormValidity])

  useEffect(() => {
    if (selected === "guardian" && showGuardianForm) setIsFormValid(checkGuardianFormValidity())
  }, [selected, showGuardianForm, checkGuardianFormValidity])

  const handleNext = useCallback(async () => {
    if (selected === "adult" && !showAdultForm) {
      setShowAdultForm(true)
      setIsFormValid(false)
    } else if (selected === "guardian" && !showGuardianForm) {
      setShowGuardianForm(true)
      setIsFormValid(false)
    } else if (selected === "adult" && showAdultForm) {
      try {
        const values = await form.validateFields()
        const formData = new FormData()
        formData.append("waiver_submission[trip_booking_id]", tripBookingId)
        formData.append("waiver_submission[submission_for]", "adult")
        formData.append("waiver_submission[waiver_participants_attributes][0][participant_type]", "adult")
        formData.append("waiver_submission[waiver_participants_attributes][0][first_name]", values.firstName || "")
        formData.append("waiver_submission[waiver_participants_attributes][0][last_name]", values.lastName || "")
        formData.append("waiver_submission[waiver_participants_attributes][0][email]", values.email || "")
        formData.append("waiver_submission[waiver_participants_attributes][0][phone_number]", values.phone?.fullNumber || "")
        const formattedDate = values.dob ? new Date(values.dob).toLocaleDateString("en-GB").replaceAll("/", "-") : ""
        formData.append("waiver_submission[waiver_participants_attributes][0][birthdate]", formattedDate)
        dispatch(postWaiverSubmission(formData)).then((res) => {
          toaster.success(res?.message)
          const docs = res?.data?.pending_waiver_submissions || []
          setWaiverSubmissionId(res?.data?.id)
          setPendingDocuments(docs)
          setShowSignDocumentModal(true)
        })
      } catch {
        // Validation errors shown by AntD
      }
    } else if (selected === "guardian" && showGuardianForm) {
      try {
        const [guardianValues] = await Promise.all([form.validateFields(), guardianForm.validateFields()])
        const values = guardianForm.getFieldsValue()
        const minors = values.minors || []
        const formData = new FormData()
        formData.append("waiver_submission[trip_booking_id]", tripBookingId)
        formData.append("waiver_submission[submission_for]", "minor")
        formData.append("waiver_submission[guardian_first_name]", guardianValues.firstName || "")
        formData.append("waiver_submission[guardian_last_name]", guardianValues.lastName || "")
        formData.append("waiver_submission[guardian_email]", guardianValues.email || "")
        formData.append("waiver_submission[guardian_phone_number]", guardianValues.phone?.fullNumber || "")
        const formattedGuardianDate = guardianValues.dob ? new Date(guardianValues.dob).toLocaleDateString("en-GB").replaceAll("/", "-") : ""
        formData.append("waiver_submission[guardian_birthdate]", formattedGuardianDate)
        formData.append("waiver_submission[relation]", guardianValues.relation || "")
        minors.forEach((minor, index) => {
          formData.append(`waiver_submission[waiver_participants_attributes][${index}][participant_type]`, "minor")
          formData.append(`waiver_submission[waiver_participants_attributes][${index}][full_name]`, minor.fullName || "")
          const formattedDate = minor.dateOfBirth ? new Date(minor.dateOfBirth).toLocaleDateString("en-GB").replaceAll("/", "-") : ""
          formData.append(`waiver_submission[waiver_participants_attributes][${index}][birthdate]`, formattedDate)
        })
        dispatch(postWaiverSubmission(formData)).then((res) => {
          toaster.success(res?.message)
          const docs = res?.data?.pending_waiver_submissions || []
          setWaiverSubmissionId(res?.data?.id)
          setPendingDocuments(docs)
          setShowSignDocumentModal(true)
        })
      } catch {
        // Validation errors shown by AntD
      }
    }
  }, [selected, showAdultForm, showGuardianForm, form, guardianForm, tripBookingId, dispatch])

  if (showSignDocumentModal) {
    return (
      <SignDocumentModal
        fullScreen
        showPageHeader
        open={true}
        uuid={uuid}
        waiverSubmissionId={waiverSubmissionId}
        pendingSubmission={pendingDocuments}
        bookingWaivers={pendingDocuments}
        onCancel={() => navigate(routes.clientBookingPath({ uuid }))}
      />
    )
  }

  if (bookingLoading || !tripBookingId) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Spin size="large" />
      </div>
    )
  }

  return (
    <Flex gap={10} vertical className={`${styles.container} flex-1`} align="center" justify="center">
      <div className="w-full p-m bg-white">
        <img src={logoSrc} alt="Digital Sportsman" className={styles.poweredLogo} />
      </div>
      <Flex className={styles.card} align="center" vertical>
        <Card>
          <Title level={3} className={styles.title}>
            {t("digital_waiver_and_contracts.waiver_form")}
          </Title>

          <WaiverDetails
            title={bookingInfo?.trip?.title || ""}
            date={bookingInfo?.booked_date || ""}
            location={bookingInfo?.trip?.address || ""}
            calendarIcon={CalendarIcon}
            addressIcon={AddressIcon}
          />

          <div className={`mt-4xl mb-s ${styles.subTitle}`}>{t("embedded.booking_confirmation.who_is_participation")}</div>

          <WaiverOptions selected={selected} onSelect={setSelected} className="mb-4xl" />

          {selected === "adult" && showAdultForm && (
            <ParentGuardianForm
              form={form}
              parentData={parentData}
              setParentData={setParentData}
              t={t}
              validatePhoneNumber={validatePhoneNumber}
              setIsFormValid={setIsFormValid}
            />
          )}
          {selected === "guardian" && showGuardianForm && (
            <>
              <ParentGuardianForm
                form={form}
                parentData={parentData}
                setParentData={setParentData}
                t={t}
                validatePhoneNumber={validatePhoneNumber}
                setIsFormValid={() => setIsFormValid(checkGuardianFormValidity())}
                showRelation={true}
              />
              <MinorGuestForm t={t} form={guardianForm} setIsFormValid={() => setIsFormValid(checkGuardianFormValidity())} />
            </>
          )}

          <Flex justify="center" className={styles.buttonContainer}>
            <Button
              color="primary"
              variant="solid"
              size="large"
              className={styles.nextButton}
              onClick={handleNext}
              disabled={!isFormValid || postLoading}
              loading={postLoading}
            >
              {t("embedded.booking_confirmation.next")}
            </Button>
          </Flex>
        </Card>
      </Flex>
    </Flex>
  )
}
